import clsx from "clsx";
import { useState, type ReactNode } from "react";
import { Zap } from "lucide-react";
import {
  AntennaMark,
  AsOfMarker,
  Button,
  EmptyState,
  HeadRail,
  PlatformIcon,
  SegmentedControl,
  Skeleton,
  compactNumber,
  formatRelativeTime,
} from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import { useDashboardStats, type TopCitiesBlock } from "../../app/hooks/useDashboardStats.js";
import { ReleaseArtwork } from "../releases/ReleaseArtwork.js";
import { RecentReleaseCard as SharedRecentReleaseCard } from "../releases/RecentReleaseCard.js";
import { TopPlaylistList } from "../shared/TopPlaylistList.js";
import { DspMiniCard, DspMiniFoot } from "../shared/DspMiniCard.js";
import { parseDomainDate } from "./platforms.js";
import styles from "./DashboardAnalytics.module.scss";


type DashboardQuery = ReturnType<typeof useDashboardStats>;

// The audience: owner/AV, manager, booking, label — INCLUDING their org sub-roles. The server ships
// effectiveRole as the STORED seat key, which for an org seat is a PREFIXED sub-role (mgmt_*/label_*/booking_*,
// 050_seed_org_role_catalog) that resolves to the manager/booking/label PARENT (backend RoleKeys.PrefixParents).
// Match the resolved parent, not the bare key, or a label_ar / mgmt_marketing / booking_agent seat is wrongly
// denied. (No single capability token expresses this touring+content-blend audience — releases:read, used by,
// is a different set — so the parent-resolution mirror is the faithful gate.) hides only for merch_manager;
// is the releases:read audience; is all analytics-tier.



const PLATFORM_LABELS: Record<string, string> = {
  spotify: "Spotify",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
  threads: "Threads",
  apple: "Apple Music",
  amazon: "Amazon Music",
};

const platformLabel = (p: string) => PLATFORM_LABELS[p] ?? p;

/** What a platform CALLS its audience. YouTube counts subscribers, not followers, and the reference labels
 *  the tile accordingly — one unit for all four printed the wrong noun under a real number. */
const followerUnit = (platform: string | null | undefined) =>
  platform === "youtube" ? "subscribers" : "followers";

const formatMonthDay = (iso: string | null | undefined) =>
  iso ? parseDomainDate(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

/**
 * Top Cities' "Last show" cell — ELAPSED MONTHS (`0 mo ago`), the reference's own rendering, not a date
 * (audit 12.14). The column exists to answer *"is this market going cold?"*, and a bare `Aug 12` makes the
 * reader do the subtraction — and do it against a year they cannot see, so a show from Aug 2024 and one from
 * last month print identically. Both `Action` and the booking lens's staleness window already reason in
 * elapsed time; this makes the cell agree with the advice next to it.
 *
 * Floors, so a show 40 days back reads "1 mo ago" rather than rounding up to 2 — the same choice
 * `holdCountdown` makes, and for the same reason: the number and any label derived from it must not disagree
 * at a boundary. A future date (a mis-stamped row) clamps to 0 rather than printing a negative age.
 */
const formatMonthsAgo = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const then = parseDomainDate(iso);
  const now = new Date();
  const months = Math.max(
    0,
    (now.getFullYear() - then.getFullYear()) * 12 +
      (now.getMonth() - then.getMonth()) -
      (now.getDate() < then.getDate() ? 1 : 0),
  );
  return `${months} mo ago`;
};

/** One analytics widget shell — the box + the four-state contract (skeleton / error+retry / empty / content).
 * Exported because Trending lives in its own module now but is the SAME family on the SAME page: two shells
 * side by side in one column is drift, and this one already owns the four states and the as-of marker. */
export function AnalyticsBox({
  id,
  kicker,
  title,
  subtitle,
  actions,
  headline,
  variant = "solid",
  platform,
  className,
  q,
  count,
  asOf,
  asOfUnderTitle = false,
  empty,
  emptyTitle,
  emptyDesc,
  children,
}: {
  id: string;
  /**
   * A mono-caps EYEBROW above the title, for a card whose heading is a sentence rather than a label
   * (Top Cities: "Top cities" over "Where your audience lives."). Passing one dresses the title as a
   * display HEADLINE — the same kicker / headline / sub lockup `RecentReleaseCard` carries, which is the
   * reference's shape for both of this page's lead cards.
   *
   * It is deliberately NOT a heading: the title below it still owns the outline and the region's
   * `aria-labelledby`, so an eyebrow can never become a second name for the same card.
   */
  kicker?: ReactNode;
  /** Omit for a headerless group — see `variant="transparent"`. */
  title?: string;
  /** The line beneath the title — what the card's numbers are made of. Pairs with `kicker`. */
  subtitle?: ReactNode;
  /** How the heading is DRESSED — the tag stays an `<h3>`, so the outline and `aria-labelledby` are
   *  unaffected (STYLE-GUIDE ruling 7: the treatment decides, not the tag). `label` is the mono-caps eyebrow
   *  lockup, for a card whose BODY carries the real headline — the release card's headline is the release's
   *  own name, which makes "Most recent release" above it a kicker rather than the heading. */
  /** Header content pinned right (Top Cities' Markets-tracked stat), stacked ABOVE the as-of marker
   *  in the head's `HeadRail` — see that component for why the order is a ruling and not a layout. */
  actions?: ReactNode;
  /**
   * The card's real headline, when its `title` is only a kicker (the release card). It belongs in the
   * HEADER's left column, under the kicker — not in the body. Put it in the body and the header is one line
   * tall, so a multi-row `actions` chip sets the header's height and shoves the headline down the card by its
   * own height. Here the row is sized by this column instead, which is taller anyway, and the chip stops
   * pushing anything.
   */
  headline?: ReactNode;
  /**
   * How much CHROME the box carries — mirrors `@cntrl/ui` `Card`'s prop of the same name.
   *
   * - `solid` (default) — paper, line, radius, padding.
   * - `transparent` — no background, border or padding: a group that reads as one object ON the page
   *   rather than a panel sitting on it. Pair with no `title` for a fully bare cluster (the socials
   *   2x2, which the reference renders as a bare rail group and not a card).
   *
   * The query states are unaffected: a transparent box still shows its skeletons, its retry and its
   * empty state, because those are about the DATA, not the chrome.
   */
  variant?: "solid" | "transparent";
  /**
   * Dress this box as a PLATFORM card (`.platform-card`): the brand mark before the title, the title in
   * the display face uppercased, and the platform's colour as a 5px bar down the left edge. Pass the
   * platform key (`spotify`, `amazon`, …).
   *
   * It is a separate prop rather than a third `variant` because it composes WITH the chrome variants
   * rather than replacing them — a platform card is still a solid card.
   */
  platform?: string;
  /** Extra class on the box — for a card that earns different chrome (the release showcase's roomier pad). */
  className?: string;
  /** The fetch behind this card. OMIT when the caller supplies already-resolved data (the roster
   *  heartbeat passes `TopCitiesCard` a block off its own composed read) — there is no loading or error
   *  state of this card's own to report, and passing a disabled query would flash its skeleton forever. */
  q?: DashboardQuery;
  count?: number;
  asOf?: string | null;
  /**
   * Render the `asOf` staleness marker as a line directly UNDER the title (in the header's left column)
   * instead of pinned top-right in the `HeadRail` (Ryan, ART-199 — "move updated Xh ago under the word
   * Spotify"). The Spotify hero uses this; a card with a right-pinned `actions` stat keeps the rail default.
   */
  asOfUnderTitle?: boolean;
  empty: boolean;
  emptyTitle: string;
  emptyDesc: string;
  children: ReactNode;
}) {
  const labelId = `${id}-heading`;
  const transparent = variant === "transparent";
  return (
    <section
      className={clsx(
        transparent ? styles.transparentBox : styles.box,
        platform && styles.platformCard,
        className,
      )}
      data-platform={platform}
      // Only claim a name we actually render. A headerless box with `aria-labelledby` pointing at an
      // element that does not exist is worse than no name at all — AT resolves it to empty.
      aria-labelledby={title ? labelId : undefined}
    >
      {title && (
        <header className={styles.head}>
          <div className={styles.headMain}>
            {kicker && <p className={styles.kicker}>{kicker}</p>}
            <h3
              id={labelId}
              className={clsx(
                styles.title,
                kicker && styles.titleLead,
                platform && styles.platformTitle,
              )}
            >
              {platform && <PlatformIcon platform={platform} size={32} className={styles.pcIcon} />}
              {title}
              {count != null && count > 0 && <span className={styles.count}>{count}</span>}
            </h3>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            {/* The staleness marker under the title (ART-199) — same LANDED gate as the rail copy. */}
            {asOfUnderTitle && (q ? q.data : true) && (
              <AsOfMarker asOf={asOf} className={styles.asOfSub} />
            )}
            {headline}
          </div>
          {(actions || (!asOfUnderTitle && (q ? q.data : true) && asOf)) && (
            <HeadRail>
              {actions}
              {/* Gated on the fetch having LANDED, so a stamp never paints over a skeleton. With no `q` the
                  caller handed us resolved data, so there is nothing to wait for. */}
              {!asOfUnderTitle && (q ? q.data : true) && <AsOfMarker asOf={asOf} />}
            </HeadRail>
          )}
        </header>
      )}
      {q?.isLoading ? (
        <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading">
          <Skeleton variant="tile" />
          <Skeleton variant="line" width="70%" />
          <Skeleton variant="line" width="45%" />
        </div>
      ) : q?.isError ? (
        <EmptyState
          title="Couldn't load"
          description={q.error instanceof ApiError ? q.error.message : "Please try again."}
          action={<Button onClick={() => void q.refetch()}>Retry</Button>}
        />
      ) : empty ? (
        <EmptyState title={emptyTitle} description={emptyDesc} />
      ) : (
        children
      )}
    </section>
  );
}

// ---- Trending ---------------------------------------------------------------------------------------
//
// MOVED. The Trending card now lives in `Trending.tsx`: the reference's card is a SPIKE FEED (discrete
// detected events, each with the series its detection ran on and a detail view), not a ranked list of the
// biggest deltas, and it had outgrown a block in this file. `trending.movers` still feeds the Spotlight's
// "top mover" headline from here's sibling read — a separate computation, deliberately.

// ---- Streaming: one card per DSP ----------------------------------------------------------------
//
// The reference has no "Streaming" card. It has a SPOTIFY card in the main column
// (`StreamingSection.tsx` — `.platform-card.pc--spotify`) and Amazon + Apple MINIS stacked in the rail
// above the socials 2x2 (`AudienceRail.tsx` — `.streaming-stack`). One card holding all three was our
// invention, and it is what buried Amazon in a sub-grid and dropped Apple entirely.
//
// Each platform's cells are the ones ITS source actually answers, which is why they differ:
//   Spotify  Monthly listeners · Followers · Editorial · Total streams
//   Amazon   Followers · Editorial (current / total)
//   Apple    Current · Editorial      <- no follower count exists for Apple at this vendor

/** `> 0 ? value : null` — the legacy's guard on every platform KPI. A vendor answering 0 for a count it
 *  does not track is saying "nothing", not "zero", and the reference renders a dash for it. */
const kpi = (v: number | null | undefined): number | null => (v != null && v > 0 ? v : null);

/** The reference's `ratioLabel` — "9 / 12", falling back to the current value when no total is given. */
function ratioLabel(current: number | null | undefined, total: number | null | undefined): string | null {
  if (current == null) return null;
  return `${compactNumber(current)} / ${compactNumber(total ?? current)}`;
}

/** Exact, separated counts — the reference prints `11,944` and `887,926`, never a compact `11.9k`. A KPI
 *  a manager reads out on a call has to be the real number. */
const exact = (v: number | null | undefined) => (v == null ? null : v.toLocaleString());

type SongWindow = "30d" | "all";
const SONG_WINDOWS = [
  { value: "30d", label: "30 days" },
  { value: "all", label: "All time" },
] as const satisfies readonly { value: SongWindow; label: string }[];

/** The Spotify hero — the reference's main-column platform card (`.platform-card.pc--spotify`). */
export function SpotifyCard() {
  const q = useDashboardStats();
  const sp = q.data?.streaming?.spotify ?? null;
  // ART-273: a 30-day / All-time toggle over the vendor's two rankings. All-time is only offered when the
  // doc carries it (null = a doc that predates the call), so the toggle never leads to an empty list.
  const [songWindow, setSongWindow] = useState<SongWindow>("30d");
  const allTime = sp?.topSongsAllTime ?? null;
  const showAllTime = songWindow === "all" && allTime != null;
  const songs = (showAllTime ? allTime : sp?.topSongs) ?? [];
  const songStreams = (t: (typeof songs)[number]) => (showAllTime ? t.streamsAllTime : t.streams28d) ?? null;
  const playlists = sp?.topPlaylists ?? [];
  // The bars are scaled against the BIGGEST track, not against the sum — the reference's `maxStreams`.
  const maxStreams = Math.max(1, ...songs.map((t) => songStreams(t) ?? 0));

  return (
    <AnalyticsBox
      id="streaming"
      platform="spotify"
      title="Spotify"
      q={q}
      asOf={sp?.asOf}
      asOfUnderTitle
      empty={!sp}
      emptyTitle="No streaming data yet"
      emptyDesc="Connect Spotify to see your streaming performance."
    >
      {sp && (
        <>
          {/* `.pc-kpis` — the reference's four, exact. */}
          <div className={styles.kpiRow}>
            <PcKpi label="Monthly listeners" value={exact(kpi(sp.monthlyListeners))} />
            <PcKpi label="Followers" value={exact(kpi(sp.followers))} />
            <PcKpi label="Editorial" value={exact(kpi(sp.playlistsEditorialCurrent))} />
            <PcKpi label="Total streams" value={exact(kpi(sp.streamsTotal))} />
          </div>

          {/* `.pc-detail` — songs and playlists side by side under a rule, 1.2fr / 1fr. */}
          <div className={styles.pcDetail}>
            <div>
              <div className={styles.subheadRow}>
                <p className={styles.subhead}>Top 10 songs</p>
                {allTime != null && (
                  <SegmentedControl
                    label="Top songs window"
                    size="sm"
                    variant="mono"
                    value={songWindow}
                    onChange={setSongWindow}
                    options={SONG_WINDOWS}
                  />
                )}
              </div>
              {songs.length > 0 ? (
                <ol className={styles.songList}>
                  {songs.map((t, i) => (
                    <li key={`${t.title}-${i}`} className={styles.song}>
                      <ReleaseArtwork
                        size="chip"
                        className={styles.songArt}
                        seed={t.title ?? ""}
                        mediaId={t.coverMediaId}
                        imageUrl={t.coverImageUrl}
                      />
                      <div className={styles.songMain}>
                        <p className={styles.songTitle} title={t.title ?? ""}>
                          {t.title || "Untitled"}
                        </p>
                        <span className={styles.songTrack}>
                          <span
                            className={styles.songFill}
                            // Length is the ONLY thing that varies — the colour is a token, in the sheet.
                            style={{
                              inlineSize: `${Math.max(0, Math.round(((songStreams(t) ?? 0) / maxStreams) * 100))}%`,
                            }}
                          />
                        </span>
                      </div>
                      <span className={styles.songCount}>{exact(songStreams(t)) ?? "—"}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className={styles.emptyNote}>Connect Spotify + Songstats to rank your top songs.</p>
              )}
            </div>

            <div>
              {/* Just the title on the dashboard (PO); the Streaming page carries the count and reach. */}
              <p className={styles.subhead}>Recent playlist adds</p>
              {playlists.length > 0 ? (
                <TopPlaylistList playlists={playlists} />
              ) : (
                <p className={styles.emptyNote}>
                  No playlist placements on file yet. Placements are tracked by Songstats and can take a day
                  or two to appear.
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </AnalyticsBox>
  );
}

/** `.kpi__label` + `.kpi__value` — the platform card's own KPI, which is bigger and exact where the shared
 *  `StatBlock` is compact. */
function PcKpi({ label, value }: { label: string; value: string | null }) {
  return (
    <div className={styles.pcKpi}>
      <span className={styles.pcKpiLabel}>{label}</span>
      <span className={styles.pcKpiValue}>{value ?? "—"}</span>
    </div>
  );
}

/**
 * Amazon + Apple, stacked in the rail above the socials 2x2 (the reference's `.streaming-stack`).
 *
 * Renders NOTHING when neither card exists — the composition already returns null for a source with none
 * of the fields these cards show, and a heading over two em-dashes is the "populated shell" this surface
 * is written to avoid.
 */
export function DspMinis() {
  const q = useDashboardStats();
  const apple = q.data?.streaming?.apple ?? null;
  const amazon = q.data?.streaming?.amazon ?? null;
  if (!apple && !amazon) return null;
  return (
    <>
      {amazon && (
        <DspMiniCard
          platform="amazon"
          name="Amazon Music"
          cells={[
            { label: "Followers", value: kpi(amazon.followers) != null ? compactNumber(amazon.followers!) : null },
            { label: "Editorial", value: ratioLabel(amazon.editorialCurrent, amazon.editorialTotal) },
          ]}
        />
      )}
      {apple && (
        <DspMiniCard
          platform="apple"
          name="Apple Music"
          cells={[
            // "Current" is the reference's label for Apple's CURRENT PLAYLIST count — the only
            // audience-ish number this source gives us. It is not a follower count, and is deliberately
            // not labelled one.
            { label: "Current", value: kpi(apple.playlistsCurrent) != null ? compactNumber(apple.playlistsCurrent!) : null },
            { label: "Editorial", value: kpi(apple.editorialCurrent) != null ? compactNumber(apple.editorialCurrent!) : null },
          ]}
        />
      )}
    </>
  );
}

export function SocialsRowCard() {
  const q = useDashboardStats();
  const tiles = q.data?.socials?.tiles ?? [];
  const anyConnected = tiles.some((t) => t.connected);
  return (
    <AnalyticsBox
      id="socials"
      // Headerless and chrome-less: the reference renders this cluster as a bare rail group, not a
      // card, and the Audience section header above now names it. No `asOf` either — Streaming's
      // marker already speaks for this data, because both cards read the SAME composed query, and two
      // markers on one read is two chances to disagree about one timestamp.
      variant="transparent"
      q={q}
      empty={!anyConnected}
      emptyTitle="No socials connected"
      emptyDesc="Connect a platform to see your reach."
    >
      {/* The tiles are the SHARED `DspMiniCard` — the same component, and therefore the same chrome, insets
          and type, as the Amazon/Apple minis stacked directly above them in this rail. The grid is the only
          thing that differs: it is what makes them half-width, and the card is the `<li>` so the width lands
          on the card itself rather than on a wrapper it has to fill. */}
      <ul className={styles.socialTiles}>
        {tiles.map((t) => (
          <DspMiniCard
            key={t.platform}
            as="li"
            platform={t.platform ?? ""}
            name={platformLabel(t.platform ?? "")}
            cells={[
              {
                // The caption carries the UNIT when there is a number, and WHY there isn't one when there is
                // not — so a dashed tile still explains itself, without a fourth line. The two dashed reasons
                // are different problems and must not share a word: "not connected" is the band's to fix (go
                // and connect it), "not synced yet" is OURS (they connected it and no figure has landed —
                // first run pending, or the vendor refusing us). Saying "not connected" on a connected
                // platform sends them to reconnect something that is already connected.
                label:
                  t.followers != null ? followerUnit(t.platform)
                  : t.connected ? "not synced yet"
                  : "not connected",
                // The `—` is passed as the VALUE rather than left null, which is what keeps the card's
                // drop-an-empty-cell rule from firing here. Deliberate, and the one place these tiles differ
                // in behaviour from the DSP minis: a socials tile is one of a fixed 2x2 that must hold its
                // shape whether or not a platform is connected, and its caption above carries the reason
                // beside the dash. A DSP mini has no grid to hold, so it drops the cell instead.
                value: t.followers != null ? compactNumber(t.followers) : "—",
              },
            ]}
            // The footnote sits inside the card's own bottom padding and costs it no height, so a tile with
            // one is exactly as tall as a tile without — and as tall as the Amazon/Apple minis above, which
            // carry none at all. Nothing here is holding space open.
            foot={
              <>
                {t.lifetimeViews != null && (
                  <DspMiniFoot>{compactNumber(t.lifetimeViews)} lifetime views</DspMiniFoot>
                )}
                {/* The last-post stamp is INSTAGRAM ONLY (item 12.3). Nothing populates `social_posts` for
                    the other three, so a stamp there could only ever be an em-dash — and the reference
                    renders no stamp at all on them rather than a dash that reads as "we looked and found
                    nothing". On Instagram, where we DO look, an empty result is a real answer and says so. */}
                {t.platform === "instagram" && (
                  <DspMiniFoot>
                    {formatRelativeTime(t.lastPostOn)
                      ? `Last post · ${formatRelativeTime(t.lastPostOn)}`
                      : "No posts yet"}
                  </DspMiniFoot>
                )}
              </>
            }
          />
        ))}
      </ul>
    </AnalyticsBox>
  );
}

// ---- Top Cities -------------------------------------------------------------------------------------

/**
 * The card's summary header (ART-100). The reference leads this card with a kicker / headline / sub
 * lockup and a right-pinned stat rail, which we had replaced with "Spotify synced" source pills and no
 * summary at all (dashboard audit 12.7).
 *
 * **Markets tracked is a SERVER count, not `cities.length`.** The table is the top ten; the stat is how
 * many markets we hold data for behind it — that gap is the reason the stat is worth a slot. Deriving it
 * from the rendered rows would print "10" for every band with a real audience and quietly re-describe the
 * table as the data. It reads `—` rather than `0` when we have nothing, per this card's `kpi` rule.
 *
 * **Total audience is deliberately absent** (PO, ART-100) even though the reference carries it: summing a
 * per-platform city rollup double-counts the same person across Spotify and Instagram, so the one figure
 * the reference put in the biggest type is the one figure here that cannot be made true.
 */
function MarketsTracked({ markets }: { markets: number }) {
  return (
    <div className={styles.tcStats}>
      <div className={styles.tcStat}>
        <span className={styles.tcStatLabel}>Markets tracked</span>
        <span className={styles.tcStatNum}>{markets > 0 ? markets.toLocaleString() : "—"}</span>
      </div>
    </div>
  );
}

/**
 * "Where your audience lives." — ONE card, on every surface that shows this table (the artist dashboard, the
 * Music tab, and the roster's Management per-artist page). Legacy made the same consolidation
 * (`top-cities-mirror`, 2026-07-06): *"the roster Management view now renders the exact artist-side Top
 * Cities table (same component + same band-scoped data)"* rather than a reduced ranked list.
 *
 * `block` is an OPTIONAL pre-fetched projection, for a caller whose payload already carries it — the roster
 * heartbeat takes `stats.TopCities` off the composed read it is already making, so letting this card fetch
 * again would add a round-trip for data in hand. Omit it on the dashboard, where this card IS the fetch.
 * Either way the rendering is identical, which is the point: two data paths, never two tables.
 */
export function TopCitiesCard({ block }: { block?: TopCitiesBlock | null } = {}) {
  // Skipped entirely when the caller supplied the block — `enabled:false` keeps react-query from firing.
  const q = useDashboardStats({ enabled: !block });
  const resolved = block ?? q.data?.topCities;
  const cities = resolved?.cities ?? [];
  const markets = resolved?.marketsTracked ?? 0;
  const maxAudience = Math.max(1, ...cities.map((c) => c.audience ?? 0));
  return (
    <AnalyticsBox
      id="top-cities"
      className={styles.topCities}
      kicker="Top cities"
      title="Where your audience lives."
      // PO copy, ART-100, with "from Bandsintown" dropped on 2026-10-01 when that integration was retired —
      // "Last show" is `artist.shows`, the band's own show history (past shows imported from Bandsintown before
      // then included). Every other clause is true of this card's sources: `audience_cities` (Songstats + Meta
      // city demographics) and Antenna's fan-geo column.
      subtitle="Spotify listeners, Instagram followers, Antenna Followers all cross-referenced with your show history"
      actions={<MarketsTracked markets={markets} />}
      // A supplied block is already resolved, so there is no loading/error state of this card's own to
      // report — its caller owns that. Passing `q` here would flash this card's skeleton over data it has.
      q={block ? undefined : q}
      asOf={resolved?.asOf}
      empty={cities.length === 0}
      emptyTitle="No city data yet"
      emptyDesc="Connect Spotify or Instagram and your strongest markets rank here with booking cues."
    >
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">City</th>
              {/* "Total Audience", not "Audience" (PO, ART-100): this column is the CROSS-PLATFORM sum of
                  the three beside it, and the bare noun read as a fourth peer source. */}
              <th scope="col" className={styles.num}>
                Total Audience
              </th>
              <th scope="col" className={styles.num}>
                Spotify
              </th>
              <th scope="col" className={styles.num}>
                IG
              </th>
              <th scope="col" className={styles.num}>
                {/* The Antenna tower marks the one column that is OURS rather than a vendor's — the three
                    beside it are platform-branded in the subtitle, and this one had nothing (PO, ART-100).
                    Decorative: the column is named by the text next to it. */}
                <span className={styles.thMark}>
                  <AntennaMark size={12} />
                  Antenna
                </span>
              </th>
              <th scope="col">Last show</th>
              <th scope="col">
                <span className={styles.srOnly}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {cities.map((c, i) => {
              const priority = (c.audience ?? 0) >= maxAudience * 0.5; // relative "priority market" bolt
              const cityQuery = encodeURIComponent(c.city ?? "");
              return (
                <tr key={`${c.city}-${c.region}-${i}`}>
                  <td>
                    <span className={styles.cityName}>
                      {priority && <Zap className={styles.bolt} aria-label="Priority market" />}
                      {c.city}
                      {c.region ? <span className={styles.cityRegion}>{c.region}</span> : null}
                    </span>
                  </td>
                  <td className={styles.num}>{c.audience != null ? compactNumber(c.audience) : "—"}</td>
                  <td className={styles.num}>{c.spotify != null ? compactNumber(c.spotify) : "—"}</td>
                  <td className={styles.num}>{c.instagram != null ? compactNumber(c.instagram) : "—"}</td>
                  {/* Antenna fan-geo column — A-track-deferred SEAM (still '—'). */}
                  <td className={styles.num}>{c.antenna != null ? compactNumber(c.antenna) : "—"}</td>
                  <td>{c.lastShowOn ? formatMonthsAgo(c.lastShowOn) : "Never"}</td>
                  {/* The Action column. A booked city states its date and offers no CTA — a market already on
                      the calendar is not one to go get. Otherwise the chip is the SERVER's verdict
                      (`BookingRouting`: audience floors + the 12-month staleness window), which the booking
                      lens renders from the same field.

                      It used to be computed here from "have you played there" alone, ignoring audience — so a
                      40-listener city we had never played read "Book", and a strong market played two years
                      ago read "Route" where the booking agent's copy of this same table said Book. One datum
                      cannot have two rules (ruled 2026-09-01). A market qualifying for nothing shows no chip
                      rather than a CTA it has not earned. */}
                  <td className={styles.actions}>
                    {c.nextShowOn ? (
                      <span className={styles.action} data-state="ok">
                        ✓ {formatMonthDay(c.nextShowOn)}
                      </span>
                    ) : c.action === "route" ? (
                      <a className={styles.action} data-state="route" href={`/booking?city=${cityQuery}&mode=route`}>
                        Route
                      </a>
                    ) : c.action === "warm" ? (
                      <span className={styles.action} data-state="warm">Warm</span>
                    ) : c.action === "book" ? (
                      <a className={styles.action} data-state="book" href={`/booking?city=${cityQuery}`}>
                        Book
                      </a>
                    ) : (
                      <span className={styles.action} data-state="none">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AnalyticsBox>
  );
}

// ---- Most Recent Release ----------------------------------------------------------------------------

/**
 * The release showcase — rendered by the SHARED `RecentReleaseCard`, the same component the Streaming
 * (Music) tab uses, over the same `RecentReleaseBlock` both reads get from
 * `AnalyticsComposition.BuildFeaturedReleaseAsync`. The backend unified the DATA so the two surfaces could
 * not drift; this is the other half of that.
 *
 * Only the query states stay here. A band with no release is NOT an empty state for this card — it keeps
 * its eight labelled rows and dashes them, with "No releases yet" standing in for the release's name — so
 * `AnalyticsBox`'s empty slot no longer applies to this widget, and it is used for the skeleton/retry pair
 * alone.
 */
export function RecentReleaseCard() {
  const q = useDashboardStats();

  if (q.isLoading || q.isError) {
    return (
      <AnalyticsBox
        id="recent-release"
        className={styles.releaseBox}
        title="Most recent release"
        q={q}
        empty={false}
        emptyTitle="No releases yet"
        emptyDesc="Your latest drop and its rollout signals show up here."
      >
        {null}
      </AnalyticsBox>
    );
  }

  return (
    <SharedRecentReleaseCard
      rr={q.data?.recentRelease ?? null}
      headingLevel="h3"
      headingId="recent-release-heading"
    />
  );
}

// ---- The composed analytics section --------------------------------------------------------------

/**
 * The dashboard analytics widgets — trending / streaming+socials / top-cities / recent-release,
 * all from ONE composed read (`useDashboardStats`) with `as_of` staleness markers and the null → `—` contract.
 * Per-widget role gating (hidden for merch_manager; owner/AV·manager·booking·label; releases:read;
 * all analytics-tier). The widgets ride the shell's already-joined `band:{id}` group — `sync-completed`
 * invalidates the shared `['band', bandId, 'dashboard-stats']` key live.
 */
/**
 * The Audience section — the reference's "streaming + socials" pair as ONE placeable widget (key
 * `audience`). Which seats see it is decided server-side by the dashboard layout; this renders it.
 *
 * The two cards sit SIDE BY SIDE (the reference's `.streaming-grid`: streaming in the main column,
 * socials in a 372px rail), not stacked. Stacking them gave the socials card the full page width,
 * which is what stretched its 2x2 into one flat row of tiles — the layout was making the grid
 * decision, not the grid.
 */
export function AudienceSection() {
  return (
    <section className={styles.audience} aria-labelledby="audience-heading">
      <h3 id="audience-heading" className={styles.audienceTitle}>
        Audience
      </h3>
      <div className={styles.audienceGrid}>
        <SpotifyCard />
        {/* The reference's `.streaming-stack`: the Amazon and Apple minis, then the socials 2x2 —
            one rail column beside the Spotify hero, not a second full-width card. */}
        <div className={styles.streamingStack}>
          <DspMinis />
          <SocialsRowCard />
        </div>
      </div>
    </section>
  );
}

